import { __awaiter } from "tslib";
import { useEffect } from 'react';
import { useAemConfig } from '@as-react/common-components';
import { useTranslation } from 'react-i18next';
import { I18N_NAMESPACES } from '../../constants/i18nConstants';
import { getIn, useFormikContext } from 'formik';
import { useIsMounted, usePrevious } from '@as/cotopaxi';
import { AddressFieldName } from '@as-react/common-types';
import GeneralAPI from '../../api/general/GeneralAPI';
import { generateFieldName, isValidAddress, isValidPostCode } from '../../util/BuyFormUtil';
import { mimicDelay } from '../../util/GeneralUtil';
import { DELAY } from '../../constants/GeneralConstants';
import { mapWithAssert } from '../../util/ArrayUtil';
export const AddressChangeHandler = ({ countryCode, countryIdFieldName, fieldPrefix = '', vatNumberFieldName = '', setAddresses, }) => {
    const { defaultRequestParams, servicesEndpoint } = useAemConfig();
    const { t } = useTranslation(I18N_NAMESPACES.buy);
    const { values, touched, isSubmitting, dirty, setFieldError, setFieldValue, setFieldTouched } = useFormikContext();
    const resetFieldValue = (field) => {
        setFieldValue(field, '');
        setFieldTouched(field, false);
    };
    const [postCodeFieldName, cityFieldName, finderFieldName, houseNumberFieldName, houseNumberAdditionFieldName, addressFieldName, addressLine2FieldName, addressLine3FieldName, addressLine4FieldName, countyFieldName, customTownFieldName, stateFieldName,] = mapWithAssert([
        AddressFieldName.POSTCODE,
        AddressFieldName.CITY,
        AddressFieldName.FINDER,
        AddressFieldName.HOUSE_NUMBER,
        AddressFieldName.HOUSE_NUMBER_ADDITION,
        AddressFieldName.ADDRESS,
        AddressFieldName.ADDRESS_LINE_2,
        AddressFieldName.ADDRESS_LINE_3,
        AddressFieldName.ADDRESS_LINE_4,
        AddressFieldName.COUNTY,
        AddressFieldName.CUSTOM_TOWN,
        AddressFieldName.STATE,
    ], name => generateFieldName(name, fieldPrefix));
    const postCode = getIn(values, postCodeFieldName);
    const postCodeTouched = getIn(touched, postCodeFieldName);
    const countryId = getIn(values, countryIdFieldName);
    const countryIdOld = usePrevious(countryId);
    const city = getIn(values, cityFieldName);
    const houseNumber = getIn(values, houseNumberFieldName);
    const vatNumber = getIn(values, vatNumberFieldName);
    const isMounted = useIsMounted();
    useEffect(() => {
        const resetAddressField = () => {
            resetFieldValue(postCodeFieldName);
            resetFieldValue(cityFieldName);
            resetFieldValue(finderFieldName);
            resetFieldValue(houseNumberFieldName);
            resetFieldValue(houseNumberAdditionFieldName);
            resetFieldValue(addressFieldName);
            resetFieldValue(addressLine2FieldName);
            resetFieldValue(addressLine3FieldName);
            resetFieldValue(addressLine4FieldName);
            resetFieldValue(countyFieldName);
            resetFieldValue(customTownFieldName);
            resetFieldValue(stateFieldName);
        };
        const forceValidationOnCountryChange = () => __awaiter(void 0, void 0, void 0, function* () {
            yield mimicDelay(DELAY.DELAY_0);
            vatNumberFieldName && setFieldTouched(vatNumberFieldName, !!vatNumber);
        });
        if (countryIdOld && String(countryId) !== String(countryIdOld)) {
            resetAddressField();
            forceValidationOnCountryChange();
        }
    }, [countryId]);
    useEffect(() => {
        const shouldRun = countryCode && ['BE', 'DE', 'FR', 'LU'].includes(countryCode);
        const validate = () => __awaiter(void 0, void 0, void 0, function* () {
            if (!isValidPostCode(postCode, countryCode)) {
                return;
            }
            try {
                const data = yield GeneralAPI.getAddresses(servicesEndpoint, defaultRequestParams, parseInt(countryId, 10), postCode);
                if (!isMounted())
                    return;
                const hasCustomCity = !!city && data.every(addressRecord => { var _a; return ((_a = addressRecord.city) === null || _a === void 0 ? void 0 : _a.toUpperCase()) !== city.toUpperCase(); });
                if (hasCustomCity) {
                    setAddresses([{ city }, ...data]);
                    setFieldValue(cityFieldName, city);
                    return;
                }
                setAddresses(data);
                if (data.length === 1) {
                    setFieldValue(cityFieldName, data[0].city);
                }
            }
            catch (_a) {
                isMounted() && setFieldError(postCodeFieldName, t('buy.error.no.results'));
            }
        });
        if (!postCodeTouched && city && shouldRun) {
            setAddresses([{ city }]);
            setFieldValue(cityFieldName, city);
        }
        if (postCode && !isSubmitting && shouldRun) {
            validate();
        }
    }, [postCode, postCodeTouched, countryCode, isMounted]);
    useEffect(() => {
        let isCurrent = true;
        const setStreetAndCity = () => __awaiter(void 0, void 0, void 0, function* () {
            if (countryCode === 'NL' && isValidAddress(postCode, houseNumber, countryCode) && dirty) {
                try {
                    const data = yield GeneralAPI.getAddresses(servicesEndpoint, defaultRequestParams, parseInt(countryId, 10), postCode, houseNumber);
                    if ((data === null || data === void 0 ? void 0 : data.length) > 0 && isMounted()) {
                        const firstAddress = data[0];
                        if (firstAddress.address) {
                            setFieldValue(addressFieldName, firstAddress.address);
                        }
                        if (firstAddress.city) {
                            setFieldValue(cityFieldName, firstAddress.city);
                        }
                        if (firstAddress.houseNumberAddition) {
                            setFieldValue(houseNumberAdditionFieldName, firstAddress.houseNumberAddition);
                        }
                    }
                }
                catch (_a) {
                    setFieldError(postCodeFieldName, t('buy.error.no.results'));
                }
            }
        });
        !!isCurrent && mimicDelay().then(() => setStreetAndCity());
        return () => {
            isCurrent = false;
        };
    }, [countryCode, postCode, houseNumber, isMounted]);
    return null;
};
